import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "./siteData";

/* -------------------------------------------------------------------------- */
/*  QUOTE FORM CONTENT  (Firestore document: site/quote)                      */
/*  The wording and choices shown in the "Request a quote" window. The public */
/*  QuoteModal reads this same document, so Publish = live.                   */
/*                                                                            */
/*  `icon` on services and property types is a key: droplets, search, gauge,  */
/*  sun, zap, wrench, package, bulb, home, sprout, building, landmark.        */
/*                                                                            */
/*  Things that stay in the code on purpose: the 47 counties, the contact     */
/*  methods (call, WhatsApp, email) because the form logic depends on them,   */
/*  and the validation messages.                                              */
/* -------------------------------------------------------------------------- */

// What the quote window shows today, so the manager opens pre-filled the first time.
export const DEFAULT_QUOTE = {
  panel: {
    title: "Request a quote",
    text: "Three quick steps. It takes about two minutes.",
    note: "Free and no obligation. We reply within one working day.",
  },
  // Always three, one for each page of the form.
  steps: [
    {
      label: "Your needs",
      short: "What you want quoted",
      title: "What do you need?",
      sub: "Pick everything that applies. You can choose more than one.",
    },
    {
      label: "The project",
      short: "Where and when",
      title: "Tell us about the site",
      sub: "This helps us price the visit and the work properly.",
    },
    {
      label: "Your details",
      short: "How to reach you",
      title: "How do we reach you?",
      sub: "We will contact you with your quote.",
    },
  ],
  services: [
    { label: "Borehole drilling", hint: "A new borehole", icon: "droplets" },
    { label: "Water survey and siting", hint: "Find where to drill", icon: "search" },
    { label: "Pump installation", hint: "Submersible or surface", icon: "gauge" },
    { label: "Solar installation", hint: "Panels, inverter, batteries", icon: "sun" },
    { label: "Solar water pumping", hint: "Pump water with the sun", icon: "zap" },
    { label: "Maintenance and repairs", hint: "Service or fix a system", icon: "wrench" },
    { label: "Pumps, panels and tanks", hint: "Buy equipment", icon: "package" },
    { label: "Not sure yet", hint: "Advise me", icon: "bulb" },
  ],
  property: [
    { label: "Home", icon: "home" },
    { label: "Farm", icon: "sprout" },
    { label: "Business", icon: "building" },
    { label: "Institution", icon: "landmark" },
  ],
  timeline: ["As soon as possible", "Within a month", "In 1 to 3 months", "Just planning"],
  // {name} becomes the visitor's first name, {contact} becomes "call you",
  // "message you on WhatsApp" or "email you", depending on what they chose.
  success: {
    title: "Request received",
    text: "Thank you, {name}. We will {contact} within one working day to arrange a site visit and send your quote.",
  },
};

const quoteRef = () => doc(db, "site", "quote");

// An older document may be missing a section, or hold an empty list: fall back per section.
const list = (value, fallback) => (Array.isArray(value) && value.length ? value : fallback);

export async function loadQuote() {
  const snap = await getDoc(quoteRef());
  if (!snap.exists()) return { data: DEFAULT_QUOTE, exists: false, updatedAt: null };
  const { updatedAt, updatedBy, ...data } = snap.data();
  return {
    data: {
      panel: { ...DEFAULT_QUOTE.panel, ...data.panel },
      steps:
        Array.isArray(data.steps) && data.steps.length === 3
          ? data.steps.map((s, i) => ({ ...DEFAULT_QUOTE.steps[i], ...s }))
          : DEFAULT_QUOTE.steps,
      services: list(data.services, DEFAULT_QUOTE.services),
      property: list(data.property, DEFAULT_QUOTE.property),
      timeline: list(data.timeline, DEFAULT_QUOTE.timeline),
      success: { ...DEFAULT_QUOTE.success, ...data.success },
    },
    exists: true,
    updatedAt: updatedAt?.toDate?.() ?? null,
  };
}

export async function saveQuote(data, by) {
  await setDoc(quoteRef(), {
    panel: data.panel,
    steps: data.steps,
    services: data.services,
    property: data.property,
    timeline: data.timeline,
    success: data.success,
    updatedAt: serverTimestamp(),
    updatedBy: by || "",
  });
}

/* -------------------------------------------------------------------------- */
/*  QUOTATION REQUESTS  (Firestore: orders/quotations/requests/{id})          */
/*  Written by the public quote form. The rules let anyone create one, but    */
/*  only admins can read, change or delete them, so this function never       */
/*  reads anything back (addDoc does not need read access).                   */
/* -------------------------------------------------------------------------- */

export async function submitQuotation(form) {
  await addDoc(collection(db, "orders", "quotations", "requests"), {
    services: form.services,
    country: form.country,
    // A county for Kenya, a region or district for other countries.
    county: form.county.trim(),
    area: form.area.trim(),
    property: form.property,
    timeline: form.timeline,
    notes: form.notes.trim(),
    name: form.name.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    method: form.method,
    status: "new",
    createdAt: serverTimestamp(),
  });
}